import { removePrefixForced, removeSuffixMaybe } from "./string-tools.js";

export const WEB_PATH_SEP = '/';
export const WEB_PATH_CURRENT_DIR = './';
export const WEB_PATH_GO_UP = '../';
export const RE_LEGAL_ROOT_REL_WEB_PATH = /^(\.\.\/)*$/;

//========== Categorization ==========

export function isAbsoluteWebPath(webPath: string) {
  return webPath.startsWith(WEB_PATH_SEP);
}

export function forceAbsWebPath(webPath: string) {
  if (!isAbsoluteWebPath(webPath)) {
    throw new TypeError('Not an absolute web path: ' + webPath);
  }
}

//========== Root paths and peer paths ==========

export const RE_PATH_TO_ROOT = /^(\.\.\/)*$/;

export function createPathToRoot(absWebPath: string) {
  const segmentCount = absWebPath.split(WEB_PATH_SEP).length;
  if (segmentCount <= 2) {
    // '/index.html', '/'
    return '';
  }
  return WEB_PATH_GO_UP.repeat(segmentCount - 2);
}

export function createPeerWebPath(srcAbsWebPath: string, destAbsWebPath: string) {
  const srcSegments = removePrefixForced(srcAbsWebPath, WEB_PATH_SEP).split(WEB_PATH_SEP);
  const destSegments = removePrefixForced(destAbsWebPath, WEB_PATH_SEP).split(WEB_PATH_SEP);
  while (srcSegments.length > 0 && destSegments.length > 0 && srcSegments[0] === destSegments[0]) {
    srcSegments.shift();
    destSegments.shift();
  }
  if (srcSegments.length === 0) {
    return destSegments.join(WEB_PATH_SEP);
  }
  return WEB_PATH_GO_UP.repeat(srcSegments.length-1) + destSegments.join(WEB_PATH_SEP);
}

//========== Navigating paths ==========

export function goToParent(webPath: string) {
  if (webPath.endsWith(WEB_PATH_SEP)) {
    return webPath;
  }

  const sepIndex = webPath.lastIndexOf(WEB_PATH_SEP);

  if (sepIndex < 0) {
    return '';
  }
  return webPath.slice(0, sepIndex + WEB_PATH_SEP.length);
}

export function removeLastSegment(webPath: string) {
  // Last possible index at which WEB_PATH_SEP can be found
  let maxIndex = webPath.length - WEB_PATH_SEP.length;
  if (webPath.endsWith(WEB_PATH_SEP)) {
    // Skip the end
    maxIndex -= WEB_PATH_SEP.length;
  }
  // Result is maxIndex or lower
  const sepIndex = webPath.lastIndexOf(WEB_PATH_SEP, maxIndex);

  if (sepIndex < 0) {
    return '';
  }
  return webPath.slice(0, sepIndex + WEB_PATH_SEP.length);
}

//========== Joining ==========

export function joinWebPaths(start: string, end: string): string {
  if (end.startsWith('/')) {
    throw new TypeError(
      'Second web path must not start with "/": ' + end
    );
  } 
  let base = goToParent(start);
  if (end.startsWith(WEB_PATH_GO_UP)) {
    do {
      end = end.slice(WEB_PATH_GO_UP.length);
      base = removeLastSegment(base);
    } while (end.startsWith(WEB_PATH_GO_UP));
    return concatWebPaths(base, end);
  }
  if (end.startsWith(WEB_PATH_CURRENT_DIR)) {
    // Ensure `end` can be processed by concatWebPaths()
    end = end.slice(WEB_PATH_CURRENT_DIR.length);
  }
  return concatWebPaths(base, end);
}

//========== Concatenating ==========

/**
 * Join this (relative or absolute) web path with another (relative!) web path.
 */
 export function concatWebPaths(baseBath: string, relPath: string): string {
  if (relPath.startsWith('/') || relPath.startsWith('./') || relPath.startsWith('../')) {
    throw new TypeError(
      'Second web path must not start with "/", "./", "../": ' + relPath
    );
  }  

  // pathToRoot can be empty
  if (baseBath.length === 0) {
    return relPath;
  }
  if (baseBath.endsWith(WEB_PATH_SEP)) {
    return baseBath + relPath;
  }
  return baseBath + WEB_PATH_SEP + relPath;
}

//========== Extracting parts of a web path ==========

const RE_EXTENSION = /\.[a-z0-9]*$/i;

export function getWebPathExtension(webPath: string) {
  webPath = removeSuffixMaybe(webPath, WEB_PATH_SEP);
  const match = webPath.match(RE_EXTENSION);
  if (match) {
    return webPath.slice(match.index);
  }
  return '';
}

export function changeWebPathExtension(webPath: string, extension: string) {
  if (!extension.startsWith('.')) {
    throw new Error('Extension must start with a dot: ' + extension);
  }
  webPath = removeSuffixMaybe(webPath, WEB_PATH_SEP);
  const match = webPath.match(RE_EXTENSION);
  if (match) {
    return webPath.slice(0, match.index) + extension;
  }
  return webPath + extension;
}

export function getWebBasename(webPath: string) {
  const sepIndex = webPath.lastIndexOf(WEB_PATH_SEP);
  if (sepIndex < 0) {
    return webPath;
  }
  return webPath.slice(sepIndex + WEB_PATH_SEP.length);
}
